import { FC, memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router';
import { Share2 } from 'lucide-react';

import { APP_ROUTES } from '@navigation/types';
import { useShareModal } from '@hooks/useShareModal';
import { useScreenSizes } from '@hooks/useScreenSizes';
import { useAuthUser } from '@redux/selectors/auth';
import { theme } from 'theme';
import { ShowType } from '@typings/show';
import { formatNumber } from '@utils/index';
import { RECORDED_SHOW_PLAYER_HEIGHT } from '@utils/constants';
import { generateShowShareData } from '@utils/share';
import { PremierText } from '@components/text';
import { useShowDataById } from '@redux/selectors/shows';
import Participants from '@components/show/participants';
import Sides from '@components/show/card/components/Sides';
import { useRecordedShow } from '@providers/RecordedShowProvider';
import ChannelView from '@components/show/menu/components/ChannelView';
import ExitButton from '@components/show/menu/components/ExitButton';
import DynamicChat from '@components/show/dynamicChat';
import backgroundImage from '@assets/images/card.png';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.black};
`;

const Header = styled.div<{ $backgroundImage?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  background-image: ${({ $backgroundImage }) =>
    $backgroundImage ? `url(${$backgroundImage})` : 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) =>
      `${theme.colors.black}${theme.opacity[60]}`};
    z-index: 1;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const MenuContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  gap: 8%;
`;

const Menu = styled.div`
  min-width: 50%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 20%;
`;

const Listeners = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 2px;
  }
`;

const Number = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.lg};
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.md};
  }
`;

const Label = styled(PremierText)`
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  }
`;

const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px 16px;
  gap: 32px;
`;

const Title = styled(PremierText)`
  flex: 1;
  font-size: ${({ theme }) => theme.fonts.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.white};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.xl};
  }
`;

const ShareButton = styled(Share2)`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 20px;
    height: 20px;
  }
`;

const Body = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-bottom: 120px;
  }
`;

const MainContent = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
  }
`;

const Content = styled.div`
  position: relative;
  width: 60%;
  height: calc(100% - ${RECORDED_SHOW_PLAYER_HEIGHT}px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    height: 100%;
  }
`;

const ChatWrapper = styled.div`
  width: 40%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-inline: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    height: auto;
    min-height: 150px;
    max-height: 50%;
    flex-direction: column;
    border-top: 1px solid ${({ theme }) => theme.colors.grey[700]};
    background-color: ${({ theme }) => theme.colors.black};
  }
`;

const RecordedShow: FC = () => {
  const { isAnonymous } = useAuthUser();
  const { id } = useParams();
  const show = useShowDataById(id);
  const navigate = useNavigate();

  const {
    closeCurrentRecordedShow,
    currentTopicIndex,
    topics,
    speakers,
    currentLeftSpeakerIndex,
    currentRightSpeakerIndex,
  } = useRecordedShow();
  // const topicId = useMemo(
  //   () => topics[currentTopicIndex]?.id,
  //   [topics, currentTopicIndex],
  // );
  const leftSpeakerId = useMemo(
    () =>
      currentLeftSpeakerIndex !== null
        ? speakers.left[currentLeftSpeakerIndex]?.id
        : null,
    [currentLeftSpeakerIndex, speakers.left],
  );
  const rightSpeakerId = useMemo(
    () =>
      currentRightSpeakerIndex !== null
        ? speakers.right[currentRightSpeakerIndex]?.id
        : null,
    [currentRightSpeakerIndex, speakers.right],
  );
  const { isMobile, isTablet } = useScreenSizes();
  const { openShareModal } = useShareModal();

  const onLeaveShow = useCallback(() => {
    closeCurrentRecordedShow();
    navigate(APP_ROUTES.HOME, { replace: true });
  }, [navigate, closeCurrentRecordedShow]);

  const onSharePress = useCallback(() => {
    if (show) {
      const shareData = generateShowShareData({
        id: show.id,
        status: show.status,
        title: show.title,
        image: show.image,
      });
      openShareModal(shareData);
    }
  }, [show, openShareModal]);

  return (
    <Container>
      <Header $backgroundImage={show?.image || backgroundImage}>
        <MenuContainer>
          <Menu>
            {!!show?.channelId && <ChannelView channelId={show.channelId} />}

            <Listeners>
              <Number>{`${formatNumber(show?.listenersCount, 0) || 0} `}</Number>

              <Label>{`Listener${show?.listenersCount === 1 ? '' : 's'}`}</Label>
            </Listeners>
          </Menu>

          <ExitButton text={'Exit'} onExitShow={onLeaveShow} />
        </MenuContainer>

        <TitleContainer>
          <Title>
            {topics?.length ? topics[currentTopicIndex]?.title : show?.title}
          </Title>

          <ShareButton onClick={onSharePress} />
        </TitleContainer>
      </Header>

      <Body>
        <MainContent>
          <Content>
            {show.type === ShowType.Sideless ? null : (
              <Sides
                blueSide={show.blueSide}
                redSide={show.redSide}
                isCard={false}
                textStyle={{
                  fontSize: isMobile
                    ? theme.fonts.fontSize.sm
                    : theme.fonts.fontSize.lg,
                  paddingBlock: '4px',
                }}
              />
            )}

            <Participants
              showId={id || ''}
              leftSpeakerId={leftSpeakerId}
              rightSpeakerId={rightSpeakerId}
              speakingUIDs={{}}
              isStatusesShown={false}
              isRecord
            />
          </Content>

          <ChatWrapper>
            <DynamicChat
              showId={id || ''}
              isShownChatForm={!isAnonymous && !isMobile && !isTablet}
            />
          </ChatWrapper>
        </MainContent>
      </Body>
    </Container>
  );
};

export default memo(RecordedShow);
